/**
 * Mixpanel Tracker
 *
 * Provides Mixpanel event tracking integration for WooAI Chatbot Pro.
 * Automatically detects existing Mixpanel installation and tracks chatbot events.
 *
 * @package WooAIChatbot
 * @since 0.3.0
 */
/**
 * Internal event to Mixpanel event name mapping
 */
const EVENT_NAME_MAP = {
    // Engagement events
    conversation_start: 'Chat Started',
    widget_open: 'Widget Opened',
    widget_close: 'Widget Closed',
    message_sent: 'Message Sent',
    message_received: 'Response Received',
    // Product events
    product_click: 'Product Clicked',
    product_view: 'Product Viewed',
    products_viewed: 'Products Viewed',
    // Ecommerce events
    add_to_cart: 'Added to Cart',
    remove_from_cart: 'Removed from Cart',
    checkout_start: 'Checkout Started',
    purchase: 'Purchase Completed',
    // Session events
    session_start: 'Session Started',
    session_end: 'Session Ended',
    // Topic events
    topic_detected: 'Topic Detected',
    playbook_started: 'Playbook Started',
    playbook_completed: 'Playbook Completed',
    // Error events
    error: 'Error Occurred',
};
/**
 * Mixpanel Tracker Class
 *
 * Singleton class for tracking events to Mixpanel.
 * Automatically detects if Mixpanel is installed on the page.
 */
class MixpanelTracker {
    constructor() {
        Object.defineProperty(this, "token", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "debugMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "eventQueue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    /**
     * Initialize the tracker with a project token
     *
     * @param token - Mixpanel project token
     * @param options - Optional configuration
     */
    init(token, options) {
        if (!token || typeof token !== 'string') {
            console.warn('[MixpanelTracker] Invalid token provided');
            return;
        }
        this.token = token;
        this.debugMode = options?.debug ?? false;
        // Check if Mixpanel SDK is already loaded
        if (window.mixpanel) {
            // SDK already loaded, just initialize
            window.mixpanel.init(token, {
                debug: this.debugMode,
                track_pageview: false,
                persistence: 'localStorage',
            });
            this.isInitialized = true;
            this.flushQueue();
        }
        else {
            // Load Mixpanel SDK dynamically
            this.loadSDK(token);
        }
    }
    /**
     * Load Mixpanel SDK dynamically
     */
    loadSDK(token) {
        const script = document.createElement('script');
        script.id = 'mixpanel-js';
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
        script.onload = () => {
            if (window.mixpanel) {
                window.mixpanel.init(token, {
                    debug: this.debugMode,
                    track_pageview: false,
                    persistence: 'localStorage',
                });
                this.isInitialized = true;
                this.flushQueue();
            }
        };
        script.onerror = () => {
            console.warn('[MixpanelTracker] Failed to load Mixpanel SDK');
        };
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        }
        else {
            document.head.appendChild(script);
        }
    }
    /**
     * Flush queued events after initialization
     */
    flushQueue() {
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            if (event) {
                this.track(event.name, event.properties);
            }
        }
    }
    /**
     * Check if Mixpanel is available on the page
     *
     * @returns True if mixpanel object exists
     */
    isAvailable() {
        return this.isInitialized && typeof window.mixpanel !== 'undefined';
    }
    /**
     * Check if tracker has been initialized
     *
     * @returns True if init() has been called with valid token
     */
    isReady() {
        return this.isInitialized && this.token !== null;
    }
    /**
     * Track an event to Mixpanel
     *
     * @param eventName - Internal event name (will be mapped to Mixpanel format)
     * @param params - Optional event parameters
     */
    track(eventName, params) {
        if (!eventName || typeof eventName !== 'string') {
            console.warn('[MixpanelTracker] Invalid event name');
            return;
        }
        // Queue events if not yet initialized
        if (!this.isAvailable()) {
            if (this.token) {
                this.eventQueue.push({ name: eventName, properties: params });
            }
            return;
        }
        // Map internal event name to Mixpanel format
        const mixpanelEventName = this.mapEventName(eventName);
        // Build event parameters
        const eventParams = {
            source: 'wooai_chatbot',
            timestamp: new Date().toISOString(),
            ...params,
        };
        try {
            window.mixpanel?.track(mixpanelEventName, eventParams);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error tracking event:', error);
            }
        }
    }
    /**
     * Track an ecommerce event with items
     *
     * @param eventName - Ecommerce event name
     * @param items - Array of items
     * @param params - Additional parameters
     */
    trackEcommerce(eventName, items, params) {
        const eventParams = {
            ...params,
            items: items,
            item_count: items.length,
        };
        // Calculate total value if not provided
        if (typeof eventParams.value !== 'number' && items.length > 0) {
            eventParams.value = items.reduce((total, item) => {
                const itemValue = (item.price ?? 0) * (item.quantity ?? 1);
                return total + itemValue;
            }, 0);
        }
        this.track(eventName, eventParams);
    }
    /**
     * Identify a user
     *
     * @param userId - User identifier
     * @param properties - Optional user properties
     */
    identify(userId, properties) {
        if (!this.isAvailable() || !userId) {
            return;
        }
        try {
            window.mixpanel?.identify(userId);
            if (properties) {
                this.setUserProperties(properties);
            }
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error identifying user:', error);
            }
        }
    }
    /**
     * Set user properties (people profile)
     *
     * @param properties - User properties to set
     */
    setUserProperties(properties) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.mixpanel?.people.set(properties);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error setting user properties:', error);
            }
        }
    }
    /**
     * Set user properties only if they haven't been set before
     *
     * @param properties - User properties to set once
     */
    setUserPropertiesOnce(properties) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.mixpanel?.people.set_once(properties);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error setting user properties once:', error);
            }
        }
    }
    /**
     * Increment numeric user properties
     *
     * @param properties - Properties to increment with their amounts
     */
    incrementUserProperties(properties) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.mixpanel?.people.increment(properties);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error incrementing user properties:', error);
            }
        }
    }
    /**
     * Register super properties (sent with every event)
     *
     * @param properties - Super properties to register
     */
    registerSuperProperties(properties) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.mixpanel?.register(properties);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error registering super properties:', error);
            }
        }
    }
    /**
     * Get the current distinct ID
     *
     * @returns The current distinct ID or null
     */
    getDistinctId() {
        if (!this.isAvailable()) {
            return null;
        }
        try {
            return window.mixpanel?.get_distinct_id() ?? null;
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error getting distinct ID:', error);
            }
            return null;
        }
    }
    /**
     * Start timing an event (for measuring duration)
     *
     * @param eventName - Event name to time
     */
    timeEvent(eventName) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.mixpanel?.time_event(this.mapEventName(eventName));
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error timing event:', error);
            }
        }
    }
    /**
     * Reset the Mixpanel instance (for logout)
     */
    reset() {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.mixpanel?.reset();
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MixpanelTracker] Error resetting:', error);
            }
        }
    }
    /**
     * Map internal event name to Mixpanel format
     *
     * @param event - Internal event name
     * @returns Mixpanel event name
     */
    mapEventName(event) {
        // Check if we have a mapping
        if (EVENT_NAME_MAP[event]) {
            return EVENT_NAME_MAP[event];
        }
        // Convert snake_case to Title Case
        const titleCase = event
            .split(/[_\s-]+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        // Prefix with WooAI if not already present
        if (!titleCase.startsWith('WooAI')) {
            return `WooAI ${titleCase}`;
        }
        return titleCase;
    }
    /**
     * Enable debug mode
     */
    enableDebug() {
        this.debugMode = true;
    }
    /**
     * Disable debug mode
     */
    disableDebug() {
        this.debugMode = false;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return {
            token: this.token,
            isInitialized: this.isInitialized,
            isAvailable: this.isAvailable(),
        };
    }
}
/**
 * Singleton instance export
 */
export const mixpanelTracker = new MixpanelTracker();
/**
 * Default export for convenience
 */
export default mixpanelTracker;
